import { ip_back } from "../config";
import { evalVideoExtension, blobWithCanonicalEvalMime } from "../utils/evalVideoEncoding";
import { evalClipFilename } from "../utils/evalClipUploadMeta";
import { assertVideoUploadAllowed } from "../utils/videoUploadNetwork";
import {
  buildCaptureQosSummary,
  getCaptureQosContext,
  setCaptureQosContext,
  trackCaptureQosUpload,
} from "../utils/captureQos";

function authHeader() {
  const token =
    (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  return token ? { Authorization: token } : {};
}

/** Backoff between attempts; the server dedupes retries by client_upload_id. */
export const EVAL_UPLOAD_RETRY_DELAYS_MS = Object.freeze([2_000, 6_000, 15_000]);

/** Network failures, timeouts, throttling and 5xx are worth another attempt. */
export function isRetryableUploadError(err) {
  const status = Number(err?.status);
  if (!Number.isFinite(status) || status <= 0) return true;
  return status === 408 || status === 429 || status >= 500;
}

export async function withUploadRetry(
  attempt,
  { delaysMs = EVAL_UPLOAD_RETRY_DELAYS_MS, onRetry = null } = {}
) {
  let lastErr = null;
  for (let i = 0; i <= delaysMs.length; i += 1) {
    try {
      return await attempt(i);
    } catch (err) {
      lastErr = err;
      if (i >= delaysMs.length || !isRetryableUploadError(err)) throw err;
      onRetry?.(err, i + 1);
      await new Promise((resolve) => setTimeout(resolve, delaysMs[i]));
    }
  }
  throw lastErr;
}

export function newClientUploadId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Upload an evaluation clip to the same mobile API iOS uses.
 */
export async function uploadEvaluationVideo({
  blob,
  mimeType,
  teId,
  playerId,
  sequenceNumber,
  recordedAt = new Date(),
  parentMessageId = null,
  clipKind = null,
  kotMatchId = null,
  tableNumber = null,
  cameraPosition = null,
  viewSetup = null,
  filename: filenameOverride = null,
  /** KoT mid-match piece: staged server-side, merged on finalize. */
  segmentIndex = null,
  finalizeCapture = false,
  clientUploadId = null,
  retry = true,
}) {
  const finalizeOnly =
    !blob && Boolean(finalizeCapture) && Boolean(String(kotMatchId || "").trim());
  if ((!blob && !finalizeOnly) || !teId) {
    throw new Error("evaluation video upload requires a clip and te_id");
  }
  assertVideoUploadAllowed();
  const priorFlow = getCaptureQosContext()?.flow;
  const flow =
    priorFlow && priorFlow !== "eval" ? priorFlow : "eval";
  setCaptureQosContext({
    flow,
    session_id: String(teId),
    ts_id: viewSetup?.ts_id || null,
  });
  const uploadBlob = blob ? blobWithCanonicalEvalMime(blob) : null;
  const ext = evalVideoExtension(mimeType || uploadBlob?.type || blob?.type || "video/mp4");
  const seq =
    sequenceNumber != null && Number(sequenceNumber) > 0
      ? Number(sequenceNumber)
      : null;
  const kotId =
    kotMatchId != null && String(kotMatchId).trim()
      ? String(kotMatchId).trim()
      : "";
  const kind = String(clipKind || "").trim().toLowerCase();
  const kindPrefix =
    kind === "stroke_slowmo" || kind === "max_rally" ? kind : "web";
  const filename =
    filenameOverride ||
    (kindPrefix !== "web"
      ? kotId
        ? `SmartTT_${kindPrefix}_kot_${kotId.slice(0, 8)}.${ext}`
        : seq
          ? `SmartTT_${kindPrefix}_seq${seq}_${String(teId).slice(0, 8)}.${ext}`
          : `SmartTT_${kindPrefix}_${String(teId).slice(0, 8)}.${ext}`
      : evalClipFilename({
          tableNumber,
          cameraPosition,
          sequenceNumber: seq,
          teId,
          ext,
          kotMatchId: kotId || null,
        }));

  const qosSummary = buildCaptureQosSummary({
    encode_wall_ms: viewSetup?.encode_wall_ms ?? null,
    client_encoded: viewSetup?.client_encoded ?? null,
    codec: viewSetup?.client_codec || "h264",
  });
  const uploadId =
    segmentIndex != null && !finalizeCapture
      ? null
      : String(clientUploadId || viewSetup?.client_upload_id || newClientUploadId());
  const stampedViewSetup =
    viewSetup && typeof viewSetup === "object"
      ? {
          ...viewSetup,
          upload_started_at:
            viewSetup.upload_started_at || new Date().toISOString(),
          capture_qos: {
            ...(viewSetup.capture_qos || {}),
            ...qosSummary,
          },
        }
      : {
          upload_started_at: new Date().toISOString(),
          capture_qos: qosSummary,
        };
  if (uploadId) stampedViewSetup.client_upload_id = uploadId;

  const send = () => trackCaptureQosUpload(
    {
      bytes: uploadBlob?.size || blob?.size || 0,
      clip_id: filename,
    },
    async () => {
      const form = new FormData();
      if (uploadBlob) form.append("videoMessage", uploadBlob, filename);
      form.append("te_id", String(teId));
      if (playerId != null && String(playerId).trim()) {
        form.append("player_id", String(playerId));
      }
      if (seq) {
        form.append("sequence_number", String(seq));
      }
      if (kotMatchId != null && String(kotMatchId).trim()) {
        form.append("kot_match_id", String(kotMatchId).trim());
      }
      if (tableNumber != null && Number(tableNumber) > 0) {
        form.append("table_number", String(Number(tableNumber)));
      }
      if (cameraPosition != null && String(cameraPosition).trim()) {
        form.append(
          "camera_position",
          String(cameraPosition).trim().toUpperCase()
        );
      }
      form.append("view_setup", JSON.stringify(stampedViewSetup));
      if (recordedAt) {
        form.append("recorded_at", new Date(recordedAt).toISOString());
      }
      if (parentMessageId != null && String(parentMessageId).trim()) {
        form.append("parent_message_id", String(parentMessageId).trim());
      }
      if (kind) {
        form.append("clip_kind", kind);
      }
      if (segmentIndex != null && Number(segmentIndex) >= 0) {
        form.append("segment_index", String(Math.floor(Number(segmentIndex))));
      }
      if (finalizeCapture) {
        form.append("finalize_capture", "1");
      }
      if (uploadId) {
        form.append("client_upload_id", uploadId);
      }

      const resp = await fetch(`${ip_back}api/mobileAPI/evaluationVideo`, {
        method: "POST",
        headers: authHeader(),
        body: form,
      });
      let json = {};
      try {
        json = await resp.json();
      } catch {
        json = {};
      }
      if (!resp.ok || json?.status === "error") {
        const msg =
          json?.message ||
          json?.error ||
          `Evaluation video upload failed (${resp.status})`;
        const err = new Error(msg);
        err.status = resp.status;
        throw err;
      }
      return json;
    }
  );

  if (!retry) return send();
  return withUploadRetry(send, {
    onRetry: (err, n) =>
      console.warn(
        `Evaluation video upload retry ${n} (${err?.status || "network"}):`,
        err?.message || err
      ),
  });
}

/** Second pass: microphone recorded beside a live H.264 clip. */
export async function uploadEvaluationVideoAudio(messageId, blob, { retry = true } = {}) {
  if (!messageId || !blob || blob.size < 256) return null;
  return postAudioForm("evaluationVideoAudio", { message_id: messageId }, blob, retry);
}

/** Microphone for one staged KoT piece; the server joins it at finalize. */
export async function uploadKotSegmentAudio({
  kotMatchId,
  segmentIndex,
  blob,
  retry = true,
}) {
  if (!kotMatchId || segmentIndex == null || !blob || blob.size < 256) return null;
  return postAudioForm(
    "kotSegmentAudio",
    {
      kot_match_id: String(kotMatchId).trim(),
      segment_index: Math.floor(Number(segmentIndex)),
    },
    blob,
    retry
  );
}

function postAudioForm(route, fields, blob, retry) {
  const ext = String(blob.type || "").includes("webm") ? "webm" : "m4a";
  const send = async () => {
    const form = new FormData();
    for (const [key, value] of Object.entries(fields)) {
      form.append(key, String(value));
    }
    form.append("audio", blob, `audio.${ext}`);
    const resp = await fetch(`${ip_back}api/mobileAPI/${route}`, {
      method: "POST",
      headers: authHeader(),
      body: form,
    });
    if (!resp.ok) {
      let message = `Evaluation audio upload failed (${resp.status})`;
      try {
        const json = await resp.json();
        if (json?.message) message = json.message;
      } catch {
        /* keep the status */
      }
      const err = new Error(message);
      err.status = resp.status;
      throw err;
    }
    return resp.json().catch(() => ({}));
  };
  return retry ? withUploadRetry(send) : send();
}
